import { Alert, Combobox } from "@playbookai/ui";
import type {
  ConversationProfileConfiguration,
  ConversationSpecialistSettings,
} from "@playbookai/player-ai-client";

function profileOptions(configuration: ConversationProfileConfiguration) {
  const defaultName =
    configuration.settings.source === "app" ||
    configuration.settings.source === "platform"
      ? configuration.settings.effective_profile_name
      : null;
  return [
    {
      value: "",
      label: defaultName
        ? `Workspace default · ${defaultName}`
        : "Workspace default",
    },
    ...configuration.profiles.map((profile) => ({
      value: profile.id,
      label: [profile.display_name, profile.provider_name, profile.model_name]
        .filter(Boolean)
        .join(" · "),
    })),
  ];
}

export function ConversationModelProfileControls({
  current,
  background,
  onCurrentChange,
  onBackgroundChange,
}: {
  current: ConversationProfileConfiguration;
  background: ConversationSpecialistSettings | null;
  onCurrentChange: (profileId: string | null) => Promise<void>;
  onBackgroundChange: (profileId: string | null) => Promise<void>;
}) {
  const options = profileOptions(current);
  return (
    <div className="mt-3 space-y-3 border-t border-pb-border pt-3">
      <div className="space-y-1.5">
        <Combobox
          label="Current chat model profile"
          value={current.settings.profile_id ?? ""}
          disabled={!current.settings.can_change}
          placeholder="Search model profiles"
          options={options}
          onChange={(value) => void onCurrentChange(value || null)}
        />
        {current.settings.fallback_reason ? (
          <Alert tone="watch">
            This chat’s selected profile is unavailable. Choose another profile.
          </Alert>
        ) : null}
      </div>
      {background ? (
        <div className="space-y-1.5">
          <Combobox
            label="Background agent model profile"
            value={background.profile_id ?? ""}
            disabled={!background.can_change}
            placeholder="Search model profiles"
            options={options}
            onChange={(value) => void onBackgroundChange(value || null)}
          />
          {background.profile_fallback_reason ? (
            <Alert tone="watch">
              The background agent’s selected profile is unavailable. Choose
              another profile.
            </Alert>
          ) : null}
        </div>
      ) : null}
      {!current.settings.can_change ? (
        <p className="text-dense-2 text-pb-text-muted">
          Set by the conversation Owner or active Facilitator.
        </p>
      ) : null}
      <p className="text-caption text-pb-text-muted">
        Changes apply to the next run. Work already in progress keeps its model.
      </p>
    </div>
  );
}
